import type { Deal, Pipeline } from './types';

/**
 * All Deals: every deal, both pipelines, one flat table.
 *
 * Kyu, 10 September: "I want another tab in there that has like all the deals
 * in it", and "I can't search a deal here."
 *
 * FLAT, with no sections, and that is forced rather than chosen. A section
 * belongs to one pipeline (deal_sections.pipeline), so there is no grouping
 * that means the same thing on both sides. Mixing them would put two different
 * "Nurture" columns on one screen and invite somebody to drag between them.
 *
 * Sorted quietest first by default. That is the list Alex asked for on the
 * same call, and it costs nothing to make this tab be it: the deals nobody has
 * touched rise to the top on their own, whatever section they are parked in.
 */

export interface DealRow {
  deal: Deal;
  /** ISO timestamp of the last activity on the deal's lead, or null. */
  lastActivityAt: string | null;
  /**
   * Whole days since that activity. NULL when we cannot say at all, which is
   * NOT the same as zero and NOT the same as a long silence. See
   * 20270128000100: a deal with no linked lead and a deal contacted this
   * morning must not read alike.
   */
  daysSilent: number | null;
}

export function daysSince(lastActivityAt: string | null, today: Date): number | null {
  if (lastActivityAt === null) return null;
  const then = new Date(lastActivityAt);
  if (Number.isNaN(then.getTime())) return null;
  const ms = today.getTime() - then.getTime();
  // Floor, not round: something logged 30 hours ago is one day of silence, not
  // "about one". Negative (a date logged ahead of now) reads as 0 rather than
  // as a negative silence, which would sort as the freshest thing on the page.
  return Math.max(0, Math.floor(ms / 86_400_000));
}

export function buildDealRows(
  deals: readonly Deal[],
  lastActivity: ReadonlyMap<string, string | null>,
  today: Date,
): DealRow[] {
  return deals.map((deal) => {
    const lastActivityAt = lastActivity.get(deal.id) ?? null;
    return { deal, lastActivityAt, daysSilent: daysSince(lastActivityAt, today) };
  });
}

/**
 * Most recent activity first, the default.
 *
 * Kyu, 10 September: "default view would be on top latest recent activity to
 * oldest activity at the bottom." So the deal touched this morning leads and
 * the one nobody has logged anything against sits at the very bottom, below
 * even the longest silence: no signal at all is older than any signal.
 *
 * Ties break on name so the order is stable between renders. Without that a
 * table where many rows share a day reshuffles on every refetch and the row
 * you were about to click moves.
 */
export function byMostRecent(a: DealRow, b: DealRow): number {
  if (a.lastActivityAt === null && b.lastActivityAt !== null) return 1;
  if (b.lastActivityAt === null && a.lastActivityAt !== null) return -1;
  if (a.lastActivityAt !== b.lastActivityAt) {
    return (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? '');
  }
  return a.deal.name.localeCompare(b.deal.name);
}

/**
 * Quietest first. The mirror of byMostRecent, kept for the day somebody wants
 * to flip the table to Alex's reading of it ("which ones went dark").
 */
export function byQuietest(a: DealRow, b: DealRow): number {
  if (a.daysSilent === null && b.daysSilent !== null) return -1;
  if (b.daysSilent === null && a.daysSilent !== null) return 1;
  if (a.daysSilent !== b.daysSilent) {
    return (b.daysSilent ?? 0) - (a.daysSilent ?? 0);
  }
  return a.deal.name.localeCompare(b.deal.name);
}

/**
 * Free-text match over the deal's name and its linked company's name.
 *
 * Every term must match somewhere, in any order, so "kodiak 2026" finds
 * "Kodiak Cakes - 2026" and typing more words narrows rather than breaks. An
 * empty or whitespace-only query matches everything rather than nothing.
 */
export function matchesDealSearch(
  row: DealRow,
  query: string,
  companyNames: ReadonlyMap<string, string>,
): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const company = row.deal.company_id === null ? '' : (companyNames.get(row.deal.company_id) ?? '');
  const haystack = `${row.deal.name} ${company}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

/**
 * Starred only, across both pipelines.
 *
 * Kyu's option three, 10 September: star a deal on either board and All Deals
 * collects them. The star stays a flag on the deal, so nothing moves and
 * nothing has to remember where a deal came from when it is unstarred. This
 * is the same list the CEO brief is fed from, seen in the Hub.
 */
/**
 * Which board the list is showing. 'all' is both, and is the default.
 *
 * A SCOPE, not a filter, and the difference decides three numbers on the
 * screen. Search and Starred narrow what you are looking at inside a board;
 * picking CG changes which board you are looking at. So the counts are taken
 * after the pipeline is applied and before the other two: "88 of 88" when you
 * switch to CG, then "12 of 88" once you search. A denominator that stayed at
 * 202 would be answering a question nobody on this screen is asking.
 */
export type PipelineScope = Pipeline | 'all';

/** The rows this board is showing, before search and Starred narrow them. */
export function inPipelineScope(
  rows: readonly DealRow[],
  scope: PipelineScope,
): readonly DealRow[] {
  if (scope === 'all') {
    return rows;
  }
  return rows.filter((row) => row.deal.pipeline === scope);
}

/**
 * Everything the table draws.
 *
 * The three narrowings compose and none of them replaces another: pipeline
 * first because it is the scope, then Starred, then the query. A deal has to
 * survive all three to be drawn.
 */
export function filterAndSortDeals(
  rows: readonly DealRow[],
  query: string,
  companyNames: ReadonlyMap<string, string>,
  starredOnly = false,
  pipeline: PipelineScope = 'all',
): DealRow[] {
  return inPipelineScope(rows, pipeline)
    .filter((row) => !starredOnly || row.deal.in_ceo_brief)
    .filter((row) => matchesDealSearch(row, query, companyNames))
    .sort(byMostRecent);
}

export function starredCount(rows: readonly DealRow[]): number {
  return rows.filter((row) => row.deal.in_ceo_brief).length;
}

/** How many of these have gone quiet past the line, for the count on the tab. */
export function silentCount(rows: readonly DealRow[], threshold: number): number {
  return rows.filter((row) => row.daysSilent === null || row.daysSilent >= threshold).length;
}

export const PIPELINE_SHORT: Record<Pipeline, string> = {
  cg_deals: 'CG',
  partnerships: 'Partnerships',
};

/** "10 Sep" for this year, "10 Sep 2025" otherwise. Null stays null. */
export function shortDate(iso: string | null, today: Date): string | null {
  if (iso === null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}
